'use client'

import React, { useState, useEffect } from 'react'
import styles from './styles.module.css'
import SimpleBlockContent from '../../../SimpleBlockContent'
import Text from '../../../Text'
import { $textPrimary, $textTertiary } from '../../../../styles/colors/semantics'
import { useRegionSubscriptions } from '../../../RegionSurfaces'
import { price } from '../../../../utils/formats'
import { CircularProgress } from '@mui/material'
import AttributionLink from '../../../AttributionLink'

function GetFasterPricing({
  eyebrow,
  title,
  subtitle,
  plans = []
}) {
  const [billingPeriod, setBillingPeriod] = useState('monthly')

  // Overmind→server-props (Phase A2.2): subscriptions come from the SSR-threaded
  // <RegionSurfacesProvider> (byte-identical to the store, which stays hydrated),
  // with an Overmind fallback for any non-page entry point.
  const { subscriptions, ensureRequested } = useRegionSubscriptions()

  useEffect(() => {
    ensureRequested()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Map subscription codes to plan indices (free, competitive, pro)
  const getSubscriptionByCode = (code) => {
    return subscriptions.find(s => s.code === code)
  }

  const formatPriceDisplay = (subscription) => {
    if (!subscription || !subscription.price) return { amount: '0', currency: '€' }

    const priceData = billingPeriod === 'annually' && subscription.price.yearly
      ? subscription.price.yearly
      : subscription.price.monthly

    if (!priceData || !priceData.priceCents) {
      return { amount: '0', currency: '€' }
    }

    // For yearly, show monthly equivalent
    const cents = billingPeriod === 'annually'
      ? Math.trunc(subscription.price.yearly.priceCents / 12)
      : priceData.priceCents

    const formatted = price(cents, priceData.currency)
    // Extract currency symbol and amount
    const match = formatted.match(/^([€$]|AU\$)?(.+)$/)
    if (match) {
      return { currency: match[1] || '', amount: match[2] }
    }
    return { amount: formatted, currency: '' }
  }

  // Map CMS plans to API subscriptions
  const getPlanWithDynamicPricing = (plan, index) => {
    // Try to match by code first, then by index
    const subscriptionCodes = ['free', 'dedicated', 'competitive']
    const subscription = getSubscriptionByCode(subscriptionCodes[index]) || subscriptions[index]

    const priceDisplay = formatPriceDisplay(subscription)
    const features = plan.features || []

    return {
      ...plan,
      name: plan.name || subscription?.name,
      description: plan.description || subscription?.description,
      priceAmount: priceDisplay.amount,
      priceCurrency: priceDisplay.currency,
      features,
      code: subscription?.code
    }
  }

  // #418: gate the loader on THIS section's OWN data (vrsSubscriptions), NOT the
  // global state.isLoading flag. vrsSubscriptions is SSR-hydrated whenever the
  // page renders this section (buildSurfaceMutations), so it is PRESENT on the
  // client's first render — SSR and client-first render content identically. The
  // loader shows only when the data is genuinely absent (SAME on both sides). The
  // old `state.isLoading` gate emitted a spinner into the SSR HTML whenever any
  // OTHER section's fetch flipped the global flag (the latency-only #418 cascade).
  if (!subscriptions.length) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.contentWrapper}>
          <div className={styles.loadingWrapper}>
            <CircularProgress color="inherit" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          {eyebrow && <Text type="heading-8" color={$textTertiary.dark}>{eyebrow}</Text>}
          {title && (
            <h2 className={styles.title}>
              <SimpleBlockContent blocks={title} />
            </h2>
          )}
          {subtitle && <div className={styles.subtitle}><Text type="regular" color={$textTertiary.dark}>{subtitle}</Text></div>}
        </div>

        <div className={styles.toggleWrapper}>
          <button
            className={billingPeriod === 'monthly' ? `${styles.toggleButton} ${styles.toggleButtonActive}` : styles.toggleButton}
            onClick={() => setBillingPeriod('monthly')}
          >
            <Text type="small">Monthly</Text>
          </button>
          <button
            className={billingPeriod === 'annually' ? `${styles.toggleButton} ${styles.toggleButtonActive}` : styles.toggleButton}
            onClick={() => setBillingPeriod('annually')}
          >
            <Text type="small">Annually</Text>
          </button>
        </div>

        <div className={styles.pricingGrid}>
          {plans.map((plan, index) => {
            const dynamicPlan = getPlanWithDynamicPricing(plan, index)
            const isFeatured = plan.featured || index === plans.length - 1
            const isFree = dynamicPlan.code === 'free' || dynamicPlan.priceAmount === '0'

            return (
              <div className={isFeatured ? `${styles.pricingCard} ${styles.pricingCardFeatured}` : styles.pricingCard} key={index}>
                {plan.badge && <span className={styles.badge}><Text type="small">{plan.badge}</Text></span>}

                <div className={styles.planHeader}>
                  <h3 className={styles.planName}><Text type="heading-5" color={$textPrimary.dark}>{dynamicPlan.name}</Text></h3>
                  <div className={styles.planDescription}><Text type="small" color={$textTertiary.dark}>{dynamicPlan.description}</Text></div>
                </div>

                <div className={styles.priceWrapper}>
                  {isFree ? (
                    <span className={styles.price}><Text type="heading-2" color={$textPrimary.dark}>Free</Text></span>
                  ) : (
                    <>
                      <span className={styles.price}><Text type="heading-2" color={$textPrimary.dark}>{dynamicPlan.priceCurrency}{dynamicPlan.priceAmount}</Text></span>
                      <span className={styles.period}><Text type="small" color={$textTertiary.dark}>/month</Text></span>
                    </>
                  )}
                </div>

                <AttributionLink
                  className={isFeatured ? `${styles.ctaButton} ${styles.ctaButtonFeatured}` : styles.ctaButton}
                  href="https://vrs.racing/signup?return_to=https%3A%2F%2Fapp.vrs.racing%2F%23%2FAccount%2FBilling"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Text type="regular">{plan.ctaText || 'Subscribe now'}</Text>
                </AttributionLink>

                {dynamicPlan.features && dynamicPlan.features.length > 0 && (
                  <div className={styles.featuresSection}>
                    <span className={styles.featuresTitle}><Text type="heading-8" color={$textPrimary.dark}>{plan.featuresTitle || "What's included:"}</Text></span>
                    <ul className={styles.featuresList}>
                      {dynamicPlan.features.map((feature, featureIndex) => (
                        <li className={styles.featureItem} key={featureIndex}>
                          <span className={isFeatured ? `${styles.checkIcon} ${styles.checkIconFeatured}` : styles.checkIcon} />
                          <Text type="small">{feature}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default GetFasterPricing
